"use strict";

// TEMP: forza la loading screen visibile per test — rimuovere dopo la review.
const FORCE_LOADER_VISIBLE = false;

const loader = document.getElementById("siteLoader");
if (!loader) {
    // nothing to do
} else {
    let lockedByConfigurator = false;
    let isReady = false;
    const MAX_WAIT_MS = 3500;
    const CONFIGURATOR_INDEX_MAX_WAIT_MS = 8000;
    const CONFIGURATOR_INDEX_READY_EVENT = "configurator:index:ready";

    const markReady = (forceDispatch = false) => {
        if (isReady && !forceDispatch) return;
        isReady = true;
        window.__siteReady = true;
        document.body.classList.add("site-ready");
        window.dispatchEvent(new CustomEvent("site:ready"));
    };

    const onDomReady = (cb) => {
        if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", cb, { once: true });
        } else {
            cb();
        }
    };

    const isInViewport = (el) => {
        if (!el?.getBoundingClientRect) return false;
        const rect = el.getBoundingClientRect();
        const viewH = window.innerHeight || document.documentElement.clientHeight || 0;
        return rect.bottom > 0 && rect.top < viewH;
    };

    const isCriticalMedia = (el) => {
        if (!el) return false;
        if (el.hasAttribute?.("data-loader-critical")) return true;
        const loading = el.getAttribute?.('loading');
        if (loading && loading.toLowerCase() !== 'lazy') return true;
        return isInViewport(el);
    };

    const waitForMedia = (root) => {
        if (!root) return Promise.resolve();
        const tasks = [];
        const images = [...root.querySelectorAll("img")];
        const videos = [...root.querySelectorAll("video")];

        images.forEach((img) => {
            if (img.complete && img.naturalWidth > 0) return;
            if (!isCriticalMedia(img)) return;
            tasks.push(new Promise((resolve) => {
                const done = () => resolve();
                img.addEventListener("load", done, { once: true });
                img.addEventListener("error", done, { once: true });
            }));
        });

        videos.forEach((video) => {
            if (video.readyState >= 1) return;
            if (!isCriticalMedia(video)) return;
            tasks.push(new Promise((resolve) => {
                const done = () => resolve();
                video.addEventListener("loadedmetadata", done, { once: true });
                video.addEventListener("error", done, { once: true });
            }));
        });

        return Promise.all(tasks);
    };

    const waitForStableLayout = (root) => {
        const tasks = [];
        if (document.readyState !== "complete") {
            tasks.push(new Promise((resolve) => {
                window.addEventListener("load", resolve, { once: true });
            }));
        }
        if (document.fonts?.ready) {
            tasks.push(document.fonts.ready.catch(() => {}));
        }
        tasks.push(waitForMedia(root));
        const waitForStableScroll = () => new Promise((resolve) => {
            let lastY = window.scrollY || window.pageYOffset || 0;
            let stableFrames = 0;
            let frames = 0;
            const maxFrames = 30;

            const check = () => {
                const nextY = window.scrollY || window.pageYOffset || 0;
                if (Math.abs(nextY - lastY) < 1) {
                    stableFrames += 1;
                } else {
                    stableFrames = 0;
                }
                lastY = nextY;
                frames += 1;

                if (stableFrames >= 2 || frames >= maxFrames) {
                    resolve();
                    return;
                }
                requestAnimationFrame(check);
            };

            requestAnimationFrame(check);
        });

        return Promise.all(tasks)
            .then(() => new Promise((resolve) => {
                requestAnimationFrame(() => requestAnimationFrame(resolve));
            }))
            .then(() => waitForStableScroll());
    };

    const show = () => {
        loader.classList.add("is-visible");
        loader.setAttribute("aria-busy", "true");
        isReady = false;
        window.__siteReady = false;
        document.body.classList.remove("site-ready");
    };

    const hide = () => {
        if (FORCE_LOADER_VISIBLE) return;
        if (lockedByConfigurator) return;
        const wasVisible = loader.classList.contains("is-visible");
        loader.classList.remove("is-visible");
        loader.setAttribute("aria-busy", "false");
        if (!isReady || wasVisible) {
            requestAnimationFrame(() => markReady(true));
        }
    };

    const isConfiguratorModel = () =>
        document.body.classList.contains("model-configuration-page")
        || !!document.getElementById("configRoot");
    const isConfiguratorIndex = () => !!document.querySelector(".configurator-main");
    const waitForConfiguratorIndexReady = () => {
        if (window.__configuratorIndexReady) {
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            let settled = false;
            const done = () => {
                if (settled) return;
                settled = true;
                window.clearTimeout(fallback);
                window.removeEventListener(CONFIGURATOR_INDEX_READY_EVENT, onReady);
                resolve();
            };
            const onReady = () => done();
            const fallback = window.setTimeout(done, CONFIGURATOR_INDEX_MAX_WAIT_MS);

            window.addEventListener(CONFIGURATOR_INDEX_READY_EVENT, onReady, { once: true });
            if (window.__configuratorIndexReady) {
                done();
            }
        });
    };

    const hideWhenStable = () => {
        if (isConfiguratorModel()) {
            markReady();
            return;
        }
        if (isConfiguratorIndex()) {
            waitForConfiguratorIndexReady()
                .then(() => {
                    hide();
                })
                .catch(() => {
                    hide();
                });
            return;
        }
        const root = document.getElementById("pageRoot") || document.body;
        const fallback = window.setTimeout(() => {
            hide();
        }, MAX_WAIT_MS);
        waitForStableLayout(root)
            .then(() => {
                window.clearTimeout(fallback);
                hide();
            })
            .catch(() => {
                window.clearTimeout(fallback);
                hide();
            });
    };

    if (FORCE_LOADER_VISIBLE) {
        show();
    }

    onDomReady(hideWhenStable);

    window.addEventListener("pageshow", (event) => {
        if (event.persisted) {
            if (!isConfiguratorModel()) {
                lockedByConfigurator = false;
            }
            hideWhenStable();
        }
    });

    window.addEventListener("configurator:loading:start", () => {
        lockedByConfigurator = true;
        show();
    });

    window.addEventListener("configurator:loading:end", () => {
        lockedByConfigurator = false;
        hide();
    });
}
